import {
  buildEffectivenessClaimLabelFromBoundary,
} from '../domain/claimReadinessPresentation';
import {
  type ClaimBoundarySummary,
  type ReportProjection,
  type ReportVisual,
  type SafetyReportArtifactDetailResponse,
  type SafetyReportOpenResponse,
  type SafetyReportRecord,
  type WorkflowClaimReadinessResponse,
} from '../services/reportsApi';

type Dict = Record<string, any>;

export interface EvidenceSummary {
  incident_cases: number | null;
  observed_signals: number | null;
  snapshots: number | null;
  clips: number | null;
  representative_items: number | null;
  media_coverage_snapshot_percent: number | null;
  media_coverage_clip_percent: number | null;
  source: string;
  missing_fields: string[];
}

function isRecord(value: unknown): value is Dict {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function asFiniteNumber(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim()) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

function hasOwn(value: unknown, key: string | number): boolean {
  return isRecord(value) && Object.prototype.hasOwnProperty.call(value, key);
}

function getAtPath(root: unknown, path: Array<string | number>): { found: boolean; value: unknown } {
  let current: unknown = root;
  for (const segment of path) {
    if (typeof segment === 'number') {
      if (!Array.isArray(current) || segment >= current.length) {
        return { found: false, value: undefined };
      }
      current = current[segment];
      continue;
    }
    if (!hasOwn(current, segment)) {
      return { found: false, value: undefined };
    }
    current = (current as Dict)[segment];
  }
  return { found: true, value: current };
}

function pickFirstNumeric(root: unknown, paths: Array<Array<string | number>>): { found: boolean; value: number | null } {
  for (const path of paths) {
    const resolved = getAtPath(root, path);
    if (!resolved.found) continue;
    return { found: true, value: asFiniteNumber(resolved.value) };
  }
  return { found: false, value: null };
}

function pickFirstArrayLength(root: unknown, paths: Array<Array<string | number>>): { found: boolean; value: number | null } {
  for (const path of paths) {
    const resolved = getAtPath(root, path);
    if (!resolved.found) continue;
    if (Array.isArray(resolved.value)) {
      return { found: true, value: resolved.value.length };
    }
    return { found: true, value: asFiniteNumber(resolved.value) };
  }
  return { found: false, value: null };
}

function visualItemValue(reportVisuals: unknown, visualId: string, itemId: string): { found: boolean; value: number | null } {
  const visuals = Array.isArray(reportVisuals) ? (reportVisuals as ReportVisual[]) : [];
  const visual = visuals.find((item) => String(item?.id || '').trim() === visualId);
  const items = Array.isArray(visual?.data?.items) ? visual?.data?.items || [] : [];
  const resolved = items.find((item) => String(item?.id || '').trim() === itemId);
  if (!resolved || !Object.prototype.hasOwnProperty.call(resolved, 'value')) {
    return { found: false, value: null };
  }
  return { found: true, value: asFiniteNumber(resolved.value) };
}

function countManifestFiles(packageManifest: unknown, matcher: (file: Dict) => boolean): { found: boolean; value: number | null } {
  const files = isRecord(packageManifest) && Array.isArray(packageManifest.files)
    ? packageManifest.files.filter(isRecord)
    : [];
  if (!files.length) return { found: false, value: null };
  let matched = 0;
  let scanned = 0;
  for (const file of files) {
    scanned += 1;
    if (matcher(file)) matched += 1;
  }
  return scanned > 0 ? { found: matched > 0, value: matched > 0 ? matched : null } : { found: false, value: null };
}

function countRepresentativeManifestFiles(packageManifest: unknown): { found: boolean; value: number | null } {
  return countManifestFiles(packageManifest, (file) => {
    const path = String(file.path || '').trim().toLowerCase();
    const role = String(file.role || '').trim().toLowerCase();
    return role.includes('representative') || path.includes('representative');
  });
}

function countSnapshotManifestFiles(packageManifest: unknown): { found: boolean; value: number | null } {
  return countManifestFiles(packageManifest, (file) => {
    const path = String(file.path || '').trim().toLowerCase();
    const role = String(file.role || '').trim().toLowerCase();
    return role.includes('snapshot')
      || path.includes('/snapshots/')
      || path.endsWith('.jpg')
      || path.endsWith('.jpeg')
      || path.endsWith('.png')
      || path.endsWith('.webp');
  });
}

function countClipManifestFiles(packageManifest: unknown): { found: boolean; value: number | null } {
  return countManifestFiles(packageManifest, (file) => {
    const path = String(file.path || '').trim().toLowerCase();
    const role = String(file.role || '').trim().toLowerCase();
    return role.includes('clip')
      || path.includes('/clips/')
      || path.endsWith('.mp4')
      || path.endsWith('.mov')
      || path.endsWith('.webm');
  });
}

function persistedRoots(persistedReport: SafetyReportRecord | null | undefined): Array<{ source: string; root: Dict }> {
  const roots: Array<{ source: string; root: Dict }> = [];
  const candidates: Array<[string, unknown]> = [
    ['persisted_report.finalized_payload', persistedReport?.finalized_payload],
    ['persisted_report.report_content', persistedReport?.report_content],
    ['persisted_report.report_payload', persistedReport?.report_payload],
  ];
  for (const [source, root] of candidates) {
    if (isRecord(root)) roots.push({ source, root });
  }
  return roots;
}

function firstNonEmpty(...values: Array<string | null | undefined>): string {
  for (const value of values) {
    const normalized = String(value || '').trim();
    if (normalized) return normalized;
  }
  return '';
}

function splitWindowRange(value?: string | null): { start: string; end: string } {
  const raw = String(value || '').trim();
  if (!raw) return { start: '', end: '' };
  const parts = raw.split(/\s+to\s+/i);
  if (parts.length >= 2) {
    return {
      start: String(parts[0] || '').trim(),
      end: String(parts.slice(1).join(' to ') || '').trim(),
    };
  }
  return { start: raw, end: '' };
}

export function resolveReportScopeWindow(
  reportWindowsVisual: ReportVisual | null | undefined,
  reportScopeWindow: { start?: string | null; end?: string | null } | null | undefined,
  artifactTimeWindow: { start?: string | null; end?: string | null } | null | undefined,
): { start: string; end: string } {
  const items = Array.isArray(reportWindowsVisual?.data?.items) ? reportWindowsVisual?.data?.items || [] : [];
  const scopeItem = items.find((item) => String(item?.id || '').trim() === 'report_scope');
  const scopeRange = splitWindowRange(String(scopeItem?.ts || ''));
  return {
    start: firstNonEmpty(reportScopeWindow?.start, scopeRange.start, artifactTimeWindow?.start),
    end: firstNonEmpty(reportScopeWindow?.end, scopeRange.end, artifactTimeWindow?.end),
  };
}

function resolveField(
  resolvers: Array<{ source: string; resolve: () => { found: boolean; value: number | null } }>,
): { value: number | null; source: string | null } {
  for (const resolver of resolvers) {
    const result = resolver.resolve();
    if (!result.found) continue;
    if (typeof result.value === 'number' && Number.isFinite(result.value)) {
      return { value: result.value, source: resolver.source };
    }
  }
  return { value: null, source: null };
}

export function buildEvidenceSummary(
  artifact: SafetyReportArtifactDetailResponse | null | undefined,
  persistedReport: SafetyReportRecord | null | undefined,
  customerEvidenceSummary: Record<string, any> | null | undefined,
  packageManifest: SafetyReportArtifactDetailResponse['export_manifest'] | null | undefined,
  technicalDetails: SafetyReportArtifactDetailResponse['technical_details'] | null | undefined,
): EvidenceSummary {
  const sourcesUsed = new Set<string>();
  const persisted = persistedRoots(persistedReport);
  const artifactRecord = (artifact || {}) as Dict;
  const technicalRecord = (technicalDetails || {}) as Dict;

  const incidentCases = resolveField([
    {
      source: 'customer_evidence_summary',
      resolve: () => pickFirstNumeric(customerEvidenceSummary, [
        ['report_counts', 'selected_incidents'],
        ['window_context', 'included_incident_cases'],
      ]),
    },
    {
      source: 'artifact.report_visuals',
      resolve: () => visualItemValue(artifactRecord.report_visuals, 'artifact_evidence_coverage', 'selected_incidents'),
    },
    {
      source: 'technical_details',
      resolve: () => pickFirstNumeric(technicalRecord, [
        ['report_counts', 'selected_incidents'],
        ['window_context', 'included_incident_cases'],
      ]),
    },
    ...persisted.map(({ source, root }) => ({
      source,
      resolve: () => pickFirstNumeric(root, [
        ['report_projection', 'report_counts', 'selected_incidents'],
        ['report_counts', 'selected_incidents'],
        ['customer_evidence_summary', 'report_counts', 'selected_incidents'],
        ['report_projection', 'window_context', 'included_incident_cases'],
        ['window_context', 'included_incident_cases'],
      ]),
    })),
    {
      source: 'artifact',
      resolve: () => pickFirstNumeric(artifactRecord, [
        ['report_projection', 'report_counts', 'selected_incidents'],
        ['report_projection', 'window_context', 'included_incident_cases'],
      ]),
    },
  ]);

  const observedSignals = resolveField([
    {
      source: 'customer_evidence_summary',
      resolve: () => pickFirstNumeric(customerEvidenceSummary, [
        ['report_counts', 'alerts_events_observed'],
        ['report_counts', 'alerts_events_observed_selected_period'],
      ]),
    },
    {
      source: 'artifact.report_visuals',
      resolve: () => visualItemValue(artifactRecord.report_visuals, 'artifact_evidence_coverage', 'alerts_observed'),
    },
    {
      source: 'technical_details',
      resolve: () => pickFirstNumeric(technicalRecord, [
        ['report_counts', 'alerts_events_observed'],
        ['report_counts', 'alerts_events_observed_selected_period'],
      ]),
    },
    ...persisted.map(({ source, root }) => ({
      source,
      resolve: () => pickFirstNumeric(root, [
        ['report_projection', 'report_counts', 'alerts_events_observed'],
        ['report_counts', 'alerts_events_observed'],
        ['customer_evidence_summary', 'report_counts', 'alerts_events_observed'],
        ['report_projection', 'report_counts', 'alerts_events_observed_selected_period'],
        ['report_counts', 'alerts_events_observed_selected_period'],
      ]),
    })),
    {
      source: 'artifact',
      resolve: () => pickFirstNumeric(artifactRecord, [
        ['report_projection', 'report_counts', 'alerts_events_observed'],
        ['report_projection', 'report_counts', 'alerts_events_observed_selected_period'],
      ]),
    },
  ]);

  const snapshots = resolveField([
    {
      source: 'customer_evidence_summary',
      resolve: () => pickFirstNumeric(customerEvidenceSummary, [
        ['evidence_breakdown', 'accessible_snapshots'],
        ['report_counts', 'accessible_snapshots'],
      ]),
    },
    {
      source: 'artifact.report_visuals',
      resolve: () => visualItemValue(artifactRecord.report_visuals, 'artifact_evidence_coverage', 'snapshots_available'),
    },
    {
      source: 'technical_details',
      resolve: () => pickFirstNumeric(technicalRecord, [
        ['evidence_breakdown', 'accessible_snapshots'],
        ['report_counts', 'accessible_snapshots'],
      ]),
    },
    ...persisted.map(({ source, root }) => ({
      source,
      resolve: () => pickFirstNumeric(root, [
        ['report_projection', 'evidence_breakdown', 'accessible_snapshots'],
        ['report_projection', 'report_counts', 'accessible_snapshots'],
        ['evidence_breakdown', 'accessible_snapshots'],
        ['report_counts', 'accessible_snapshots'],
        ['customer_evidence_summary', 'evidence_breakdown', 'accessible_snapshots'],
      ]),
    })),
    {
      source: 'artifact',
      resolve: () => pickFirstNumeric(artifactRecord, [
        ['report_projection', 'evidence_breakdown', 'accessible_snapshots'],
        ['report_projection', 'report_counts', 'accessible_snapshots'],
      ]),
    },
    {
      source: 'package_manifest',
      resolve: () => countSnapshotManifestFiles(packageManifest),
    },
  ]);

  const clips = resolveField([
    {
      source: 'customer_evidence_summary',
      resolve: () => pickFirstNumeric(customerEvidenceSummary, [
        ['evidence_breakdown', 'accessible_clips'],
        ['report_counts', 'accessible_clips'],
      ]),
    },
    {
      source: 'artifact.report_visuals',
      resolve: () => visualItemValue(artifactRecord.report_visuals, 'artifact_evidence_coverage', 'clips_available'),
    },
    {
      source: 'technical_details',
      resolve: () => pickFirstNumeric(technicalRecord, [
        ['evidence_breakdown', 'accessible_clips'],
        ['report_counts', 'accessible_clips'],
      ]),
    },
    ...persisted.map(({ source, root }) => ({
      source,
      resolve: () => pickFirstNumeric(root, [
        ['report_projection', 'evidence_breakdown', 'accessible_clips'],
        ['report_projection', 'report_counts', 'accessible_clips'],
        ['evidence_breakdown', 'accessible_clips'],
        ['report_counts', 'accessible_clips'],
        ['customer_evidence_summary', 'evidence_breakdown', 'accessible_clips'],
      ]),
    })),
    {
      source: 'artifact',
      resolve: () => pickFirstNumeric(artifactRecord, [
        ['report_projection', 'evidence_breakdown', 'accessible_clips'],
        ['report_projection', 'report_counts', 'accessible_clips'],
      ]),
    },
    {
      source: 'package_manifest',
      resolve: () => countClipManifestFiles(packageManifest),
    },
  ]);

  const representativeItems = resolveField([
    {
      source: 'technical_details',
      resolve: () => pickFirstArrayLength(technicalRecord, [
        ['evidence_gallery', 'total_items'],
        ['evidence_gallery', 'items'],
      ]),
    },
    {
      source: 'customer_evidence_summary',
      resolve: () => pickFirstArrayLength(customerEvidenceSummary, [
        ['evidence_gallery', 'total_items'],
        ['evidence_gallery', 'items'],
      ]),
    },
    ...persisted.map(({ source, root }) => ({
      source,
      resolve: () => pickFirstArrayLength(root, [
        ['evidence_gallery', 'total_items'],
        ['evidence_gallery', 'items'],
        ['customer_evidence_summary', 'evidence_gallery', 'total_items'],
        ['customer_evidence_summary', 'evidence_gallery', 'items'],
      ]),
    })),
    {
      source: 'artifact',
      resolve: () => pickFirstArrayLength(artifactRecord, [
        ['evidence_gallery', 'total_items'],
        ['evidence_gallery', 'items'],
      ]),
    },
    {
      source: 'package_manifest',
      resolve: () => countRepresentativeManifestFiles(packageManifest),
    },
  ]);

  [
    incidentCases.source,
    observedSignals.source,
    snapshots.source,
    clips.source,
    representativeItems.source,
  ].filter(Boolean).forEach((source) => {
    sourcesUsed.add(String(source));
  });

  const observedValue = observedSignals.value;
  const snapshotCoverage = typeof observedValue === 'number' && observedValue > 0 && snapshots.value !== null
    ? (snapshots.value / observedValue) * 100
    : null;
  const clipCoverage = typeof observedValue === 'number' && observedValue > 0 && clips.value !== null
    ? (clips.value / observedValue) * 100
    : null;

  const summary: EvidenceSummary = {
    incident_cases: incidentCases.value,
    observed_signals: observedSignals.value,
    snapshots: snapshots.value,
    clips: clips.value,
    representative_items: representativeItems.value,
    media_coverage_snapshot_percent: snapshotCoverage,
    media_coverage_clip_percent: clipCoverage,
    source: sourcesUsed.size ? Array.from(sourcesUsed).join(' + ') : 'signed_off_package_data_unavailable',
    missing_fields: [],
  };

  const missingFields: Array<keyof Omit<EvidenceSummary, 'source' | 'missing_fields'>> = [
    'incident_cases',
    'observed_signals',
    'snapshots',
    'clips',
    'representative_items',
  ];
  summary.missing_fields = missingFields.filter((field) => summary[field] === null);

  return summary;
}

export function resolveFrozenArtifactClaimBoundary(
  reportProjection?: ReportProjection | null,
): ClaimBoundarySummary | null {
  const boundary = reportProjection?.workflow_state?.claim_readiness?.claim_boundary;
  if (!boundary || typeof boundary !== 'object') return null;
  return boundary;
}

export function hasCanonicalFrozenClaimReadiness(
  reportProjection?: ReportProjection | null,
): boolean {
  const claimReadiness = reportProjection?.workflow_state?.claim_readiness;
  if (!claimReadiness || typeof claimReadiness !== 'object') return false;
  const controls = claimReadiness.controls;
  const totalCount = claimReadiness.total_count;
  return (Array.isArray(controls) && controls.length > 0) || Number(totalCount || 0) > 0;
}

export function buildArtifactEffectivenessClaimLabel(
  boundary?: ClaimBoundarySummary | null,
): string {
  return buildEffectivenessClaimLabelFromBoundary(boundary);
}

export function resolveArtifactClaimBoundaryPresentation(args: {
  frozenBoundary?: ClaimBoundarySummary | null;
  liveClaimReadiness?: WorkflowClaimReadinessResponse | null;
  legacyClaimBoundaryText?: string | null;
  legacyEffectivenessClaimBoundary?: string | null;
}): {
  canonicalBoundary: ClaimBoundarySummary | null;
  claimBoundaryText: string;
  effectivenessClaimLabel: string;
  hasCanonical: boolean;
} {
  const frozenBoundary = String(args.frozenBoundary?.wording || '').trim()
    ? args.frozenBoundary || null
    : null;
  const liveBoundary = args.liveClaimReadiness?.ok ? args.liveClaimReadiness.claim_boundary || null : null;
  const liveHasCanonical = Boolean(
    args.liveClaimReadiness?.ok
    && (
      (Array.isArray(args.liveClaimReadiness.controls) && args.liveClaimReadiness.controls.length > 0)
      || Number(args.liveClaimReadiness.total_count || 0) > 0
      || String(liveBoundary?.wording || '').trim()
    ),
  );
  const canonicalBoundary = liveHasCanonical && String(liveBoundary?.wording || '').trim()
    ? liveBoundary
    : frozenBoundary;
  const hasCanonical = Boolean(canonicalBoundary && String(canonicalBoundary.wording || '').trim());
  if (hasCanonical && canonicalBoundary) {
    return {
      canonicalBoundary,
      hasCanonical: true,
      claimBoundaryText: String(canonicalBoundary.wording || '').trim(),
      effectivenessClaimLabel: buildEffectivenessClaimLabelFromBoundary(canonicalBoundary),
    };
  }
  return {
    canonicalBoundary: null,
    hasCanonical: false,
    claimBoundaryText: String(
      args.legacyClaimBoundaryText
      || args.legacyEffectivenessClaimBoundary
      || 'Corrective-action effectiveness is not claimed unless validation gates support it.',
    ).trim(),
    effectivenessClaimLabel: /validated|may be claimed/i.test(String(args.legacyEffectivenessClaimBoundary || ''))
      ? 'Validated effectiveness'
      : 'Not allowed',
  };
}

function readPersistedReportContent(report?: SafetyReportRecord | null): Dict {
  if (!report) return {};
  return (
    (report.report_content && typeof report.report_content === 'object' ? report.report_content : null)
    || (report.report_payload && typeof report.report_payload === 'object' ? report.report_payload : null)
    || (report.finalized_payload && typeof report.finalized_payload === 'object' ? report.finalized_payload : null)
    || {}
  ) as Dict;
}

function readStoredTechnicalDetails(content: Dict): Dict {
  return (
    (isRecord(content.comprehensive_technical_details) ? content.comprehensive_technical_details : null)
    || (isRecord(content.technical_details) ? content.technical_details : null)
    || {}
  ) as Dict;
}

function buildBootstrapProjection(
  report: SafetyReportRecord,
  technicalDetails: Dict,
  timeWindow: { start?: string | null; end?: string | null } | null,
  workflowId: string | null,
  reportId: string | null,
): ReportProjection | null {
  const storedProjection = isRecord((report.report_content as Dict | undefined)?.report_projection)
    ? (report.report_content as Dict).report_projection
    : isRecord((report.report_payload as Dict | undefined)?.report_projection)
      ? (report.report_payload as Dict).report_projection
      : isRecord((report.finalized_payload as Dict | undefined)?.report_projection)
        ? (report.finalized_payload as Dict).report_projection
        : null;
  if (storedProjection) return storedProjection as unknown as ReportProjection;
  if (!technicalDetails || !Object.keys(technicalDetails).length) return null;

  return {
    schema_version: 'safety_report_projection.v1',
    report_mode: 'workflow',
    report_id: report.report_id || reportId || null,
    report_status: report.status || null,
    finalization_status: String(report.status || '').trim().toLowerCase() === 'finalized' ? 'completed' : null,
    customer_summary: isRecord(technicalDetails.customer_summary) ? technicalDetails.customer_summary : null,
    report_claims: Array.isArray(technicalDetails.report_claims)
      ? technicalDetails.report_claims.filter(isRecord)
      : [],
    workflow_state: isRecord(technicalDetails.workflow_state) ? technicalDetails.workflow_state : null,
    report_counts: isRecord(technicalDetails.report_counts) ? technicalDetails.report_counts : null,
    window_context: {
      ...(isRecord(technicalDetails.window_context) ? technicalDetails.window_context : {}),
      selected_report_window: {
        start: timeWindow?.start || null,
        end: timeWindow?.end || null,
      },
    },
    evidence_breakdown: isRecord(technicalDetails.evidence_breakdown) ? technicalDetails.evidence_breakdown : null,
    label_bundle: isRecord(technicalDetails.label_bundle) ? technicalDetails.label_bundle : null,
    source_context: isRecord(technicalDetails.source_context) ? technicalDetails.source_context : null,
    workflow_anchor: {
      workflow_id: report.workflow_id || workflowId || null,
      representative_incident_id: isRecord(report.workflow_anchor)
        ? ((report.workflow_anchor as Dict).representative_incident_id || null)
        : null,
    },
    anchor_incident_id: isRecord(report.workflow_anchor)
      ? ((report.workflow_anchor as Dict).representative_incident_id || null)
      : null,
  } as unknown as ReportProjection;
}

export function hasRenderableArtifactPayload(
  payload?: SafetyReportArtifactDetailResponse | null,
): boolean {
  if (!payload || typeof payload !== 'object') return false;
  if (isRecord(payload.technical_details) && Object.keys(payload.technical_details).length > 0) return true;
  if (isRecord(payload.report_projection) && Object.keys(payload.report_projection).length > 0) return true;
  if (isRecord(payload.evidence_gallery) && Object.keys(payload.evidence_gallery).length > 0) return true;
  if (isRecord(payload.impact_measurement_graph) && Object.keys(payload.impact_measurement_graph).length > 0) return true;
  if (isRecord(payload.decision_readiness) && Object.keys(payload.decision_readiness).length > 0) return true;
  if (isRecord(payload.comparative_safety_intelligence) && Object.keys(payload.comparative_safety_intelligence).length > 0) return true;
  if (isRecord(payload.ehs_review_worksheet) && Object.keys(payload.ehs_review_worksheet).length > 0) return true;
  if (isRecord(payload.safety_scan_buyer_report) && Object.keys(payload.safety_scan_buyer_report).length > 0) return true;
  if (typeof payload.customer_report_markdown === 'string' && payload.customer_report_markdown.trim()) return true;
  if (Array.isArray(payload.report_visuals) && payload.report_visuals.length > 0) return true;
  return false;
}

export function buildBootstrapArtifactDetail(
  openResponse?: SafetyReportOpenResponse | null,
  reportId?: string | null,
): SafetyReportArtifactDetailResponse | null {
  const report = openResponse?.report;
  if (!openResponse?.ok || !report) return null;

  const content = readPersistedReportContent(report);
  const anchor = isRecord(report.workflow_anchor) ? report.workflow_anchor : {};
  const scope = (
    (isRecord(content.scope) ? content.scope : null)
    || (isRecord(anchor.scope) ? anchor.scope : null)
    || {}
  ) as Dict;
  const zones = Array.isArray(scope.zones) ? scope.zones : [];
  const timeWindow = (
    (isRecord(content.time_window) ? content.time_window : null)
    || (isRecord(anchor.time_window) ? anchor.time_window : null)
    || null
  ) as { start?: string | null; end?: string | null } | null;
  const technicalDetails = readStoredTechnicalDetails(content);
  const evidenceGallery = (
    (isRecord(technicalDetails.evidence_gallery) ? technicalDetails.evidence_gallery : null)
    || (isRecord(content.evidence_gallery) ? content.evidence_gallery : null)
    || null
  ) as SafetyReportArtifactDetailResponse['evidence_gallery'];
  const impactMeasurementGraph = (
    (isRecord(technicalDetails.impact_measurement_graph) ? technicalDetails.impact_measurement_graph : null)
    || (isRecord(content.impact_measurement_graph) ? content.impact_measurement_graph : null)
    || null
  ) as SafetyReportArtifactDetailResponse['impact_measurement_graph'];
  const bootstrapProjection = buildBootstrapProjection(
    report,
    technicalDetails,
    timeWindow,
    report.workflow_id || openResponse.workflow_id || null,
    report.report_id || reportId || null,
  );
  const exportManifest = isRecord(content.export_manifest)
    ? content.export_manifest
    : {
      package_type: 'safety_report',
      report_status: report.status || 'finalized',
      finalization_status: String(report.status || '').trim().toLowerCase() === 'finalized' ? 'completed' : 'pending',
      files: [],
      package_display: {
        preparation_status_label: 'Completed',
        report_status_label: String(report.status || '').trim().toLowerCase() === 'finalized' ? 'Finalized' : null,
        package_type_label: 'safety_report',
        file_count: 0,
      },
    };

  return {
    ok: true,
    persisted_report: report,
    artifact: {
      report_id: report.report_id || reportId || null,
      status: report.status || 'finalized',
      status_label: 'Signed off',
      finalized_at: report.finalized_at || null,
      created_at: report.created_at || null,
      updated_at: report.updated_at || null,
      workflow_id: report.workflow_id || openResponse.workflow_id || null,
      finding_id: report.finding_id || null,
      representative_incident_id: anchor.representative_incident_id || null,
      scope: {
        site_id: report.site_id || scope.site_id || null,
        zones: zones.map((zone) => String(zone || '')).filter(Boolean),
        risk_type: scope.risk_type || null,
        device_id: scope.device_id || (anchor as Dict).device_id || null,
        camera_id: scope.camera_id || (anchor as Dict).camera_id || null,
      },
      time_window: timeWindow,
      display_labels: {
        site_label: report.site_id || null,
        area_label: zones[0] ? String(zones[0]) : (scope.zone_id ? String(scope.zone_id) : null),
        scope_reference_label: report.report_id || reportId || null,
      },
    },
    report_projection: bootstrapProjection,
    technical_details: Object.keys(technicalDetails).length ? technicalDetails : null,
    export_manifest: exportManifest,
    customer_report_markdown: String(
      content.comprehensive_report_markdown
      || content.customer_report_markdown
      || '',
    ).trim() || null,
    report_visuals: Array.isArray(content.report_visuals) ? content.report_visuals : [],
    evidence_gallery: evidenceGallery,
    impact_measurement_graph: impactMeasurementGraph,
    recommendation_snapshot: isRecord(content.recommendation_snapshot)
      ? content.recommendation_snapshot
      : isRecord(technicalDetails.recommendation_snapshot)
        ? technicalDetails.recommendation_snapshot
      : null,
    decision_readiness: isRecord(technicalDetails.decision_readiness)
      ? technicalDetails.decision_readiness
      : null,
    comparative_safety_intelligence: isRecord(technicalDetails.comparative_safety_intelligence)
      ? technicalDetails.comparative_safety_intelligence
      : null,
    ehs_review_worksheet: isRecord(technicalDetails.ehs_review_worksheet)
      ? technicalDetails.ehs_review_worksheet
      : null,
    safety_scan_buyer_report: isRecord(technicalDetails.safety_scan_buyer_report)
      ? technicalDetails.safety_scan_buyer_report
      : null,
  };
}
